import { motion } from 'framer-motion';

const fadeUp = {
	hidden: { opacity: 0, y: 16 },
	visible: {
		opacity: 1,
		y: 0,
		transition: { duration: 0.5, ease: 'easeOut' },
	},
};

interface PolicySection {
	heading?: string;
	paragraphs?: string[];
	bullets?: string[];
	subSections?: { label: string; bullets: string[] }[];
}

const sections: PolicySection[] = [
	{
		paragraphs: [
			'JAPFA COMFEED INDIA PVT. LTD. ("JAPFA", "Company", "we", "our", or "us") is committed to protecting the privacy and security of information collected through the Vehicle Management System ("VMS"), including its mobile application and web portal (collectively referred to as the "Platform").',
			'This Privacy Policy describes how we collect, use, store, process, and protect information when users access or use the Platform.',
			'By using the Platform, you agree to the collection and use of information in accordance with this Privacy Policy.',
		],
	},
	{
		heading: '2. Purpose of the Application',
		paragraphs: [
			'The Vehicle Management System (VMS) is designed to support Vehicle management like gate entry and exit processes, dispatch operations (Loading/Unloading), logistics monitoring, driver management, and operational reporting within the Company.',
			'The application is intended solely for authorized business and operational purposes.',
		],
	},
	{
		heading: '3. Information We Collect',
		paragraphs: [
			'Depending on your role and usage of the Platform, we may collect the following information:',
		],
		subSections: [
			{
				label: 'A. Personal Information',
				bullets: [
					'Full Name',
					'Employee ID (where applicable)',
					'Mobile Number',
					'Email Address',
					'Department or Business Unit',
					'User Role and Access Permissions',
				],
			},
			{
				label: 'B. Vehicle and Driver Information',
				bullets: [
					'Vehicle Registration Number',
					'Vehicle Type and Details',
					'Driver Name',
					'Driver Contact Number',
					'Driver License Information (where applicable)',
					'Transporter Information',
				],
			},
			{
				label: 'C. Operational Information',
				bullets: [
					'Vehicle Entry and Exit Records',
					'Gate Pass Details',
					'Loading and Unloading Information',
					'Dispatch Information',
					'Delivery Information',
					'Vehicle Status Updates',
					'Vehicle Turnaround Time (TAT) Records',
					'Operational Remarks and Notes',
				],
			},
			{
				label: 'D. Device and Technical Information',
				bullets: [
					'Device Model',
					'Device Operating System',
					'Application Version',
					'IP Address',
					'Browser Information',
					'Login Activity',
					'System Logs',
					'Error Reports',
				],
			},
		],
	},
	{
		heading: '4. Location Information',
		paragraphs: [
			'The Platform may collect location information from mobile devices when location-based features are enabled and authorized by the user.',
			'Location information may be used for:',
		],
		bullets: [
			'Security and compliance purposes',
			'Dispatch management',
			'Logistics optimization',
			'Operational reporting',
		],
	},
	{
		heading: '5. Camera Access',
		paragraphs: [
			'The Platform may request access to the device camera to:',
		],
		bullets: [
			'Capture vehicle photographs',
			'Upload delivery proof documents',
			'Capture gate entry and exit records',
			'Upload operational documents and images',
			'Support verification and audit processes',
		],
	},
	{
		heading: '6. Storage Access',
		paragraphs: ['The Platform may request access to device storage for:'],
		bullets: [
			'Uploading images and documents',
			'Downloading reports and operational records',
			'Storing application-related files required for business operations',
		],
	},
	{
		heading: '7. How We Use Information',
		paragraphs: [
			'Information collected through the Platform may be used to:',
		],
		bullets: [
			'Manage vehicle movements',
			'Process vehicle entry and exit activities',
			'Track dispatch and transportation operations',
			'Monitor logistics performance',
			'Generate operational reports',
			'Improve operational efficiency',
			'Investigate incidents and exceptions',
			'Maintain system security',
			'Provide technical support',
			'Comply with legal and regulatory requirements',
		],
	},
	{
		heading: '8. Data Sharing and Disclosure',
		paragraphs: [
			'We do not sell personal information.',
			'Information may be shared only under the following circumstances:',
		],
		subSections: [
			{
				label: 'Authorized Internal Users',
				bullets: [
					'Information may be accessed by authorized employees, administrators, logistics teams, security personnel, and management personnel who require access for legitimate business purposes.',
				],
			},
			{
				label: 'Service Providers',
				bullets: [
					'Cloud hosting',
					'Technical support',
					'Application maintenance',
					'Data storage',
					'System monitoring',
				],
			},
			{
				label: 'Legal Requirements',
				bullets: [
					'Applicable laws',
					'Government authorities',
					'Court orders',
					'Regulatory requirements',
					'Law enforcement agencies',
				],
			},
		],
	},
	{
		heading: '9. Data Retention',
		paragraphs: ['We retain information only for as long as necessary to:'],
		bullets: [
			'Support business operations',
			'Maintain operational records',
			'Fulfill legal obligations',
			'Conduct audits',
			'Resolve disputes',
			'Enforce company policies',
		],
	},
	{
		heading: '10. Data Security',
		paragraphs: [
			'We implement reasonable administrative, technical, and organizational safeguards to protect information against unauthorized access, disclosure, alteration, or destruction.',
			'Security measures may include:',
		],
		bullets: [
			'Secure authentication mechanisms',
			'Role-based access control',
			'Password protection',
			'Encrypted communications',
			'Audit logs and activity monitoring',
			'Periodic security reviews',
		],
	},
	{
		heading: '11. User Responsibilities',
		paragraphs: ['Users are responsible for:'],
		bullets: [
			'Maintaining confidentiality of login credentials',
			'Protecting authorized devices',
			'Providing accurate information',
			'Reporting unauthorized access or suspected security incidents',
			'Using the Platform only for authorized business activities',
		],
	},
	{
		heading: "12. Children's Privacy",
		paragraphs: [
			'The Platform is intended solely for authorized business users and is not directed toward individuals under the age of 18 years.',
			'We do not knowingly collect personal information from children.',
		],
	},
	{
		heading: '13. Your Rights',
		paragraphs: ['Subject to applicable laws, users may request:'],
		bullets: [
			'Access to their personal information',
			'Correction of inaccurate information',
			'Deletion of information where legally permissible',
			'Restriction of processing under certain circumstances',
		],
	},
	{
		heading: '14. Account Deletion Request',
		paragraphs: [
			'Users may request deletion of their account and associated personal information by contacting the VMS Support Team.',
			'Certain operational, audit, legal, security, or compliance records may be retained where required by applicable laws or legitimate business requirements.',
			'For account deletion requests, contact: buddyassist.jcipl@japfa.com',
		],
	},
	{
		heading: '15. Changes to this Privacy Policy',
		paragraphs: [
			'We may update this Privacy Policy from time to time to reflect operational, legal, regulatory, or technological changes.',
			"The latest version will always be available through the Platform and on the Company's website.",
			'Continued use of the Platform after any updates constitutes acceptance of the revised Privacy Policy.',
		],
	},
	{
		heading: '16. Contact Us',
		paragraphs: [
			'If you have questions regarding this Privacy Policy or the handling of your information, please contact:',
			'JAPFA COMFEED INDIA PVT. LTD.\nPlot No. 91, Sakore Nagar\nViman Nagar\nPune – 411014\nIndia',
			'Email: buddyassist.jcipl@japfa.com',
			'Website: https://www.japfaindia.com',
		],
	},
];

const VMSPrivacyPolicy = () => {
	return (
		<>
			<section className="flex justify-center bg-[#160700] py-[60px] ps-8 align-middle lg:ps-14 xl:py-[80px]">
				<motion.div
					className="my-auto flex flex-col items-start justify-center sm:w-[60%] md:w-[60%]"
					initial="hidden"
					whileInView="visible"
					viewport={{ once: true }}
					variants={fadeUp}
				>
					<h1 className="text-[28px] font-bold leading-[120%] text-white lg:text-[40px] xl:text-[52px]">
						Privacy Policy – Vehicle Management System (VMS)
					</h1>
				
				</motion.div>
			</section>

			{/* Policy Body */}
			<section className="bg-white px-6 py-12 sm:px-10 md:px-16 lg:px-24 xl:px-32">
				<div className="">
					{/* Introduction paragraph before numbered sections */}
					<motion.div
						className="mb-6"
						initial="hidden"
						whileInView="visible"
						viewport={{ once: true }}
						variants={fadeUp}
					>
						<h2 className="mb-2 text-[16px] font-bold text-[#1b1b1b]">
							1. Introduction
						</h2>
						{sections[0].paragraphs?.map((p, i) => (
							<p
								key={i}
								className="mb-3 text-justify text-[15px] leading-[1.75] text-[#1b1b1b]"
							>
								{p}
							</p>
						))}
					</motion.div>

					{/* Remaining sections */}
					{sections.slice(1).map((section, sIdx) => (
						<motion.div
							key={sIdx}
							className="mb-6"
							initial="hidden"
							whileInView="visible"
							viewport={{ once: true, amount: 0.1 }}
							variants={fadeUp}
						>
							{section.heading && (
								<h2 className="mb-2 text-[16px] font-bold text-[#1b1b1b]">
									{section.heading}
								</h2>
							)}

							{section.paragraphs?.map((p, i) => (
								<p
									key={i}
									className="mb-3 whitespace-pre-line text-justify text-[15px] leading-[1.75] text-[#1b1b1b]"
								>
									{p}
								</p>
							))}

							{section.bullets && section.bullets.length > 0 && (
								<ul className="mb-3 ml-5 list-disc space-y-1">
									{section.bullets.map((b, i) => (
										<li
											key={i}
											className="text-[15px] leading-[1.75] text-[#1b1b1b]"
										>
											{b}
										</li>
									))}
								</ul>
							)}

							{section.subSections?.map((sub, subIdx) => (
								<div key={subIdx} className="mb-4 ml-2">
									<p className="mb-1 text-[15px] font-semibold text-[#1b1b1b]">
										{sub.label}
									</p>
									<ul className="ml-5 list-disc space-y-1">
										{sub.bullets.map((b, i) => (
											<li
												key={i}
												className="text-[15px] leading-[1.75] text-[#1b1b1b]"
											>
												{b}
											</li>
										))}
									</ul>
								</div>
							))}
						</motion.div>
					))}
				</div>
			</section>
		</>
	);
};

export default VMSPrivacyPolicy;
